import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { apiGetCodPolicy, apiUpdateCodPolicy } from "../api";
import SettingsPanel from "../components/SettingsPanel";
import { THEME as C } from "../components/dashboardTheme";
import SettingHelp from "./Settings/settingsHelp";
import { ReadOnlyNotice } from "./Settings/settingsUi";
import { AppButton } from "../components/ui";

/**
 * Super-admin screen to view / change the Cash-on-Delivery product-price limit.
 * Reads and writes /api/cod-policy (the PUT is admin-gated server-side). The
 * saved value takes effect within ~30s everywhere (resolveCodMaxProductAmount
 * cache), across all vendor types.
 *
 * Styled with SettingsPanel so it matches the cart screen rather than being a
 * bare bootstrap card.
 */
const CodPolicySettings = ({ canEdit = false }) => {
  const [amount, setAmount] = useState("");
  const [current, setCurrent] = useState(null);
  const [defaultValue, setDefaultValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiGetCodPolicy();
      const live = Number(data?.maxProductAmount);
      setCurrent(Number.isFinite(live) ? live : null);
      setDefaultValue(Number(data?.default) || null);
      setAmount(Number.isFinite(live) ? String(live) : "");
    } catch (err) {
      toast.error("Could not load the cash on delivery limit.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const parsed = Number(amount);
  const valid = Number.isFinite(parsed) && parsed > 0;
  /* Nothing to save when the field already equals what is live. Saying so
     beats accepting a no-op write and toasting success. */
  const unchanged = valid && current != null && Math.floor(parsed) === current;
  const isDefault = current != null && current === defaultValue;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Enter an amount in UGX above 0.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiUpdateCodPolicy(Math.floor(parsed));
      const live = Number(data?.maxProductAmount);
      setCurrent(live);
      toast.success(`Items priced above UGX ${live.toLocaleString()} now need online payment.`);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Could not save the limit. Changing it needs a super administrator."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPanel
      icon="fa-money-bill-wave"
      title="Cash on delivery limit"
      subtitle={
        loading
          ? "Loading…"
          : current != null
            ? `Items above UGX ${current.toLocaleString()} must be paid online. Applies to every vendor type.`
            : "Items above the limit must be paid online. Applies to every vendor type."
      }
      action={
        canEdit && defaultValue != null && !isDefault ? (
          <AppButton
            variant="quiet"
            icon="fas fa-rotate-left"
            onClick={() => setAmount(String(defaultValue))}
            disabled={saving}
          >
            Use the server default
          </AppButton>
        ) : null
      }
    >
      <form onSubmit={handleSave}>
        {!canEdit && <ReadOnlyNotice level="manage" />}
        <SettingsPanel.Section title="What this does" icon="fa-circle-info">
          <p
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: 1.6,
              color: C.textMid,
            }}
          >
            A product whose catalogue price for one unit is{" "}
            <strong style={{ color: C.text }}>above</strong> this amount cannot be
            paid by cash on delivery, so the customer pays online before the
            vendor accepts the order. It limits losses on valuable orders that
            are never collected, and applies to every order type within about 30
            seconds of saving. A second limit on the whole basket's goods value
            is set on the server, not on this page.
            <SettingHelp topic="codBasketLimit" />
          </p>
        </SettingsPanel.Section>

        <SettingsPanel.Section
          title={<>Highest item price payable in cash<SettingHelp topic="codProductLimit" /></>}
          icon="fa-sliders"
        >
          {loading ? (
            <div style={{ padding: "24px 0", textAlign: "center", color: C.textLight }}>
              <i className="fas fa-circle-notch fa-spin" style={{ fontSize: 20 }} />
            </div>
          ) : (
            <>
              {/* One control, styled as a unit: the UGX chip and the field
                  share a border so they read as a single input, the way the
                  cart's quantity stepper does. */}
              <div
                style={{
                  display: "flex",
                  alignItems: "stretch",
                  border: `1px solid ${C.border}`,
                  borderRadius: C.radiusSm,
                  overflow: "hidden",
                  background: C.panel,
                  maxWidth: 340,
                }}
              >
                <div
                  style={{
                    padding: "0 14px",
                    display: "flex",
                    alignItems: "center",
                    background: C.stripBg,
                    borderRight: `1px solid ${C.border}`,
                    fontSize: 12,
                    fontWeight: 700,
                    color: C.textMid,
                    letterSpacing: "0.06em",
                  }}
                >
                  UGX
                </div>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={defaultValue != null ? String(defaultValue) : ""}
                  required
                  disabled={saving || !canEdit}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: "none",
                    outline: "none",
                    padding: "11px 14px",
                    fontSize: 15,
                    fontWeight: 600,
                    color: C.text,
                    background: "transparent",
                  }}
                />
              </div>

              <div style={{ fontSize: 12, color: C.textLight, marginTop: 6, lineHeight: 1.5 }}>
                Per unit, from the catalogue. A price exactly at the limit can still be paid in cash.
              </div>

              <div style={{ marginTop: 16 }}>
                <SettingsPanel.Row
                  label="Currently live"
                  value={current != null ? `UGX ${current.toLocaleString()}` : "Not loaded"}
                />
                {defaultValue != null ? (
                  <SettingsPanel.Row
                    label="Server default"
                    value={`UGX ${defaultValue.toLocaleString()}`}
                    muted
                  />
                ) : null}
                <SettingsPanel.Divider />
                <SettingsPanel.Row
                  label="After saving"
                  value={valid ? `UGX ${Math.floor(parsed).toLocaleString()}` : "Enter an amount"}
                  strong
                />
              </div>

              {canEdit && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 12,
                  marginTop: 18,
                }}
              >
                {unchanged ? (
                  <span style={{ fontSize: 12, color: C.textLight }}>
                    Already the live value.
                  </span>
                ) : null}
                <SettingsPanel.Action
                  type="submit"
                  disabled={saving || !valid || unchanged}
                >
                  {saving ? (
                    <>
                      <i className="fas fa-circle-notch fa-spin" style={{ fontSize: 12 }} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check" style={{ fontSize: 12 }} />
                      Save limit
                    </>
                  )}
                </SettingsPanel.Action>
              </div>
              )}
            </>
          )}
        </SettingsPanel.Section>
      </form>
    </SettingsPanel>
  );
};

export default CodPolicySettings;
